import { ReactNode, useMemo, useState } from 'react';
import { Box, Card, CardProps, Fade, LinkBox, NotificationProps, ProgressBar, ProgressBarProps } from '@bitrise/bitkit';
import { useInterval } from 'usehooks-ts';
import RetryNotification from '@/components/RetryNotification/RetryNotification';
import {
  calculateDuration,
  getBuildStatusColors,
  isBuildRunning,
  Build,
  PublicApiBuild,
  calculateProgressPercentage,
} from '@/models/Build';
import ColorBar from '@/components/ColorBar/ColorBar';
import { BuildItemProps } from '@/components/BuildListItem/types/BuildItemProps';
import BuildItemUpperSection from '@/components/BuildListItem/BuildItemUpperSection';
import BuildItemLowerSection from '@/components/BuildListItem/BuildItemLowerSection';
import AppForAppPage from '@/pages/App/types/AppForAppPage';

type BuildProgressBarProps = {
  build: Build;
  previousBuild?: Build | PublicApiBuild;
} & Partial<ProgressBarProps>;
const BuildProgressBar = ({ build, previousBuild }: BuildProgressBarProps): JSX.Element => {
  const [progress, setProgress] = useState(0);
  const previousBuildDuration = useMemo(() => previousBuild && calculateDuration(previousBuild), [previousBuild]);

  useInterval(
    () => {
      if (!previousBuildDuration) {
        return;
      }

      const actualBuildDuration = calculateDuration(build);
      setProgress(calculateProgressPercentage(actualBuildDuration, previousBuildDuration));
    },
    build ? 1000 : null,
  );

  return (
    <Fade in={build && isBuildRunning(build)}>
      <ProgressBar value={progress} position="absolute" top="0" left="0" right="0" />
    </Fade>
  );
};

type BuildListErrorItemProps = {
  message: ReactNode;
  retry: VoidFunction;
} & Omit<NotificationProps, 'status'>;
const BuildListErrorItem = ({ message, retry, ...rest }: BuildListErrorItemProps): JSX.Element => (
  <RetryNotification status="error" onRetry={() => retry?.()} {...rest}>
    {message}
  </RetryNotification>
);

type BuildListItemProps = {
  buildItem: BuildItemProps;
  app?: AppForAppPage;
} & CardProps;

const BuildListItem = ({ buildItem, app, ...rest }: BuildListItemProps): JSX.Element => {
  const { build, previousBuild, showAppInfo, canRunBuild, onRebuildClick, onAbortClick } = buildItem;

  return (
    <Card as={LinkBox} overflow="hidden" role="group" display="flex" borderRadius="8" withHover {...rest}>
      <ColorBar color={getBuildStatusColors(build).color} />
      <Box position="relative" flexGrow={1} flexShrink={1} minWidth="0">
        {isBuildRunning(build) && <BuildProgressBar build={build} previousBuild={previousBuild} />}
        <BuildItemUpperSection
          build={build}
          app={app}
          showAppInfo={showAppInfo}
          canRunBuild={canRunBuild}
          onRebuildClick={onRebuildClick}
          onAbortClick={onAbortClick}
        />
        <BuildItemLowerSection build={build} showBuildNumberOnMobile={!showAppInfo} />
      </Box>
    </Card>
  );
};

export { BuildListErrorItem };
export default BuildListItem;
